import { useState } from "react";

import { formatAccountRoleLabel } from "../data/accounts";
import {
  supportSessionMinutesRemaining,
  type SupportSession,
} from "../data/support-session";
import { formatDisplayTime } from "../lib/display-time";
import { useSupportSession } from "./SupportSessionProvider";

// The act-as banner. Non-dismissible by construction: there is no close control, only Exit
// support, which ends the session server-side. An operator cannot hide the fact that they are
// walking a tenant as somebody else; that is the entire point of the control.
//
// Every clause degrades honestly. An unknown display name says "a member" rather than inventing
// one; an account name the projection did not carry is simply omitted rather than guessed at.

/**
 * Static limits disclosure (D-12 / P-14). Host gates remain authoritative.
 * Shipped vs proposed must stay distinct so the banner never pretends a Later ban is enforced.
 */
export const SUPPORT_SESSION_LIMITS = [
  {
    id: "approve_service_week",
    label: "Approve a service week",
    enforcement: "shipped" as const,
    note: "Host refuses before target lookup (SWAF).",
  },
  {
    id: "include_unscheduled_candidate",
    label: "Include an unscheduled service candidate",
    enforcement: "shipped" as const,
    note: "Host refuses with service-week approval (SWAF).",
  },
  {
    id: "export_proof_of_service",
    label: "Export proof of service",
    enforcement: "shipped" as const,
    note: "Host refuses on the PoS path.",
  },
  {
    id: "export_client_artifact",
    label: "Export a Daily Activity Report or other client artifact",
    enforcement: "shipped" as const,
    note: "Client artifact paths reuse the PoS support refusal.",
  },
  {
    id: "reject_or_set_timesheet_entry",
    label: "Reject or supervisor-set a timesheet entry",
    enforcement: "proposed" as const,
    note: "Later host gate. Existing target-authority writes are unchanged until that lane ships.",
  },
  {
    id: "export_payroll",
    label: "Export payroll",
    enforcement: "proposed" as const,
    note: "Later (lane 1 desk). Not host-enforced from this banner.",
  },
] as const;

/** "Supporting as Dana Reyes · Guard · Stryke Team Elite · ends Aug 4, 2026, 12:30 PM EDT" */
export function formatSupportSessionSummary(
  session: SupportSession,
  timeZone: string,
  accountNameHint: string | null = null,
): string {
  const accountName = session.targetAccountName ?? accountNameHint;
  const parts = [
    `Supporting as ${session.targetDisplayName ?? "a member"}`,
    formatAccountRoleLabel(session.targetRole),
  ];
  if (accountName !== null && accountName.length > 0) {
    parts.push(accountName);
  }
  parts.push(`ends ${formatDisplayTime(session.expiresAt, timeZone)}`);
  return parts.join(" · ");
}

export function SupportSessionBanner({
  session,
  timeZone,
  accountNameHint = null,
}: {
  session: SupportSession;
  timeZone: string;
  accountNameHint?: string | null;
}) {
  const { end } = useSupportSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const minutesLeft = supportSessionMinutesRemaining(session);

  async function exitSupport(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    const outcome = await end();
    if (!outcome.ok) {
      // Still supporting — the banner stays up and says so.
      setError(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <div className="supportbar" role="status" aria-label="Support session">
      <span className="supportbar__badge" aria-hidden="true">
        Support
      </span>
      <span className="supportbar__text">
        {formatSupportSessionSummary(session, timeZone, accountNameHint)}
        <span className="supportbar__remaining">
          {" "}
          ({minutesLeft} min left)
        </span>
      </span>
      {error !== null ? (
        <span className="supportbar__error" role="alert">
          {error}
        </span>
      ) : null}
      <button
        className="supportbar__btn supportbar__btn--secondary"
        type="button"
        aria-expanded={limitsOpen}
        aria-controls="supportbar-limits"
        onClick={() => setLimitsOpen((open) => !open)}
      >
        What this session cannot do
      </button>
      <button className="supportbar__btn" type="button" onClick={() => void exitSupport()} disabled={busy}>
        {busy ? "Ending…" : "Exit support"}
      </button>
      {limitsOpen ? (
        <div id="supportbar-limits" className="supportbar__limits">
          <p className="supportbar__limits-lead">
            Host RPCs still authorize each act. This list is product copy, not a permission grant.
          </p>
          <ul>
            {SUPPORT_SESSION_LIMITS.map((item) => (
              <li key={item.id}>
                <strong>{item.label}</strong>
                <span>
                  {" "}
                  ({item.enforcement === "shipped" ? "Host refuses today" : "Proposed / Later"}):{" "}
                  {item.note}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
